import { loadJwt } from "../dwclient/localstorage/jwtStorage";
// Use relative URL for proxy support in development, absolute URL in production
const ORCHESTRATOR_URL = process.env.ORCHESTRATOR_URL || "/orchestrator";
const IS_DEV = !process.env.ORCHESTRATOR_URL || process.env.ORCHESTRATOR_URL === "/orchestrator";
console.log("[OrchestratorClient] ORCHESTRATOR_URL configured as:", ORCHESTRATOR_URL);
async function getAuthHeaders() {
    const jwt = await loadJwt();
    const headers = {
        "Content-Type": "application/json",
    };
    if (jwt) {
        headers["Authorization"] = `Bearer ${jwt}`;
    }
    return headers;
}
export async function initBatch(files, graphId) {
    const headers = await getAuthHeaders();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);
    try {
        const response = await fetch(`${ORCHESTRATOR_URL}/batch/init`, {
            method: "POST",
            headers,
            body: JSON.stringify({ files, graphId }),
            signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
            throw new Error(`Failed to init batch: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Batch init timeout after 5 minutes');
        }
        throw error;
    }
}
export async function getNextFile(batchId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/next`, {
        method: "GET",
        headers,
    });
    if (!response.ok) {
        throw new Error(`Failed to get next file: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function completeFile(batchId, fileIndex, uploadedBytes) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/complete/${fileIndex}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ uploadedBytes }),
    });
    if (!response.ok) {
        throw new Error(`Failed to complete file: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function failFile(batchId, fileIndex, error, retry = false) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/fail/${fileIndex}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ error, retry }),
    });
    if (!response.ok) {
        throw new Error(`Failed to mark file as failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function cancelBatch(batchId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/cancel`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
    });
    if (!response.ok) {
        throw new Error(`Failed to cancel batch: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function pauseBatch(batchId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/pause`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
    });
    if (!response.ok) {
        throw new Error(`Failed to pause batch: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function resumeBatch(batchId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/resume`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
    });
    if (!response.ok) {
        throw new Error(`Failed to resume batch: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export async function getBatchStatus(batchId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ORCHESTRATOR_URL}/batch/${batchId}/status`, {
        method: "GET",
        headers,
    });
    if (!response.ok) {
        throw new Error(`Failed to get batch status: ${response.status} ${response.statusText}`);
    }
    return response.json();
}
export class OrchestratorWebSocket {
    ws = null;
    batchId;
    onProgress;
    onComplete;
    onError;
    reconnectAttempts = 0;
    maxReconnectAttempts = 5;
    reconnectDelay = 1000;
    constructor(batchId, onProgress, onComplete, onError) {
        this.batchId = batchId;
        this.onProgress = onProgress;
        this.onComplete = onComplete;
        this.onError = onError;
    }
    connect() {
        // Skip WebSocket in proxy mode - it overloads webpack-dev-server with large uploads
        // The onBatchProgress callback in processUploadQueue handles progress updates instead
        if (ORCHESTRATOR_URL.startsWith("/")) {
            console.log(`WebSocket skipped for batch ${this.batchId} (proxy mode - using HTTP callbacks)`);
            return;
        }
        const wsUrl = `${ORCHESTRATOR_URL.replace(/^http/, "ws")}/ws/batch/${this.batchId}/progress`;
        console.log(`WebSocket connecting to: ${wsUrl}`);
        this.ws = new WebSocket(wsUrl);
        this.ws.onopen = () => {
            console.log(`WebSocket connected for batch ${this.batchId}`);
            this.reconnectAttempts = 0;
        };
        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === "progress") {
                    this.onProgress(data);
                }
                else if (data.type === "complete") {
                    this.onComplete(data);
                }
                else if (data.type === "cancelled") {
                    this.onComplete({ ...data, status: "cancelled" });
                }
                else if (data.type === "error") {
                    this.onError(new Error(data.message));
                }
            }
            catch (error) {
                console.error("WebSocket message parse error:", error);
            }
        };
        this.ws.onerror = (event) => {
            console.warn("WebSocket error (will fall back to HTTP polling):", event);
        };
        this.ws.onclose = (event) => {
            console.log(`WebSocket closed for batch ${this.batchId}, code: ${event.code}`);
            if (event.code !== 1000 && event.code !== 1001) {
                console.log("WebSocket unavailable - using HTTP polling for progress");
            }
        };
    }
    tryReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = this.reconnectDelay * this.reconnectAttempts;
            console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            setTimeout(() => this.connect(), delay);
        }
    }
    sendPing() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: "ping" }));
        }
    }
    requestStatus() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: "status" }));
        }
    }
    close() {
        this.maxReconnectAttempts = 0;
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
}
let currentBatchId = null;
let currentWebSocket = null;
export function getCurrentBatchId() {
    return currentBatchId;
}
export function setCurrentBatchId(batchId) {
    currentBatchId = batchId;
}
export function getCurrentWebSocket() {
    return currentWebSocket;
}
export function setCurrentWebSocket(ws) {
    if (currentWebSocket) {
        currentWebSocket.close();
    }
    currentWebSocket = ws;
}
export function closeCurrentWebSocket() {
    if (currentWebSocket) {
        currentWebSocket.close();
        currentWebSocket = null;
    }
}
export async function isOrchestratorAvailable() {
    const result = await checkOrchestratorHealth();
    return result.available;
}
/**
 * Fetches /health and returns structured availability + degraded-config info.
 *
 * HTTP 200  → { available: true,  degraded: false }
 * HTTP 503  → { available: true,  degraded: true, configWarnings: [...] }
 * network/timeout → { available: false, degraded: false }
 *
 * The 503 path is intentional: the server returns 503 when configWarnings exist
 * so standard uptime monitors fire without custom body parsing.  The dashboard
 * surfaces configWarnings so the operator can act without having to curl.
 */
export async function checkOrchestratorHealth() {
    const healthUrl = `${ORCHESTRATOR_URL}/health`;
    console.log("[OrchestratorClient] Checking orchestrator health at:", healthUrl);
    try {
        const startTime = Date.now();
        const response = await fetch(healthUrl, {
            method: "GET",
            signal: AbortSignal.timeout(15000),
        });
        const elapsed = Date.now() - startTime;
        // Always parse the body — we need it for both 200 and 503 responses.
        let body = {};
        try {
            body = await response.json();
        }
        catch {
            console.log("[OrchestratorClient] Could not parse health response body");
        }
        console.log("[OrchestratorClient] Health check response:", {
            url: healthUrl,
            status: response.status,
            ok: response.ok,
            elapsed: `${elapsed}ms`,
            healthStatus: body.status,
        });
        if (response.status === 503 && body.status === "degraded") {
            const warnings = Array.isArray(body.configWarnings)
                ? body.configWarnings
                : [];
            console.warn("[OrchestratorClient] Orchestrator is DEGRADED — config warnings from /health:", warnings);
            return { available: true, degraded: true, configWarnings: warnings };
        }
        return { available: response.ok, degraded: false };
    }
    catch (error) {
        console.error("[OrchestratorClient] Health check failed (orchestrator unreachable):", {
            url: healthUrl,
            error: error instanceof Error ? error.message : String(error),
        });
        return { available: false, degraded: false };
    }
}
